import { eventInRange, eventInRangeV2 } from './events';
import moment from 'moment';
import { asWallClockIn, getApptType } from 'utils/common';
import {
  formatProviderName,
  formatUsername,
  parseUsername
} from 'utils/format';
import { getTimeSlots } from 'time-slots-generator';
import { MAX_CALENDAR_TIME, MIN_CALENDAR_TIME } from 'utils/constants/calendar';
import { APPOINTMENT_HEADER_COLORS } from 'utils/constants/theme';
import { availabilityColorsMap } from 'utils/mappers/response/availabilityEventsColorsMap';
import { MapProviderPetAllergies } from 'utils/common';
import { TRANSPORTATION_SELECT } from 'utils/constants/tables/staff';
import ThreeDot from 'assets/img/DotsThreeVertical.png';
import { constructProgramDataForDisplay } from 'utils/common';
import { mapCertificationOptions } from 'utils/mappers/form';

/** @todo this function has V2 */

export const PROVIDER_LOCATIONS = {
  '3': 'In Clinic and Telehealth',
  '1': 'Telehealth Only',
  '2': 'In Clinic Only',
  '5': 'Offsite and Telehealth',
  '7': 'Any Availability'
};

const parseColor = input => {
  if (!input) return null;
  const str = String(input).trim();
  let r, g, b;
  if (str[0] === '#') {
    let h = str.slice(1);
    if (h.length === 3)
      h = h
        .split('')
        .map(c => c + c)
        .join('');
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else {
    const m = str.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    [r, g, b] = m[1].split(',').map(x => parseFloat(x));
  }
  if ([r, g, b].some(v => Number.isNaN(v))) return null;
  return [r, g, b];
};

export const toLightTint = (input, amount = 0.86) => {
  const rgb = parseColor(input);
  if (!rgb) return '#f0f0f0';
  const mix = c => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(rgb[0])}, ${mix(rgb[1])}, ${mix(rgb[2])})`;
};

export const toDarkShade = (input, amount = 0.4) => {
  const rgb = parseColor(input);
  if (!rgb) return '#3e5766';
  const mix = c => Math.round(c * (1 - amount));
  return `rgb(${mix(rgb[0])}, ${mix(rgb[1])}, ${mix(rgb[2])})`;
};
export const getStaffMemberEvents = (events, apptTypes, range) => {
  const filtered = [];
  events.forEach(event => {
    if (eventInRange(event, range)) {
      const appointmentType = getApptType(event.appointmentType, apptTypes);

      filtered.push({
        ...event,
        start: new Date(event.startTime),
        end: new Date(event.endTime),
        title: parseUsername(event.client?.name),
        desc: { main: appointmentType?.title },
        color: appointmentType?.headerColor || APPOINTMENT_HEADER_COLORS.ADMIN
      });
    }
  });
  return filtered;
};

/**
 * @param timeZone IANA zone the events should be drawn on — the provider's
 *   clinic. Omitted, events fall back to the viewer's browser clock.
 *
 * AXON-3603: the availability template is stored as bare wall-clock strings and
 * is drawn at face value, i.e. always on the provider's clock. Appointments are
 * instants, so drawing them on the viewer's clock put the two on different
 * clocks in one grid. They are put on the provider's clock here, upstream of
 * everything that reshapes or counts them.
 *
 * The range test is given the drawn position rather than the stored instant, so
 * it judges an appointment by where it will be placed. AXON-3585 widened that
 * test to the whole day, so the two agree: an appointment is dropped only for
 * being outside the week on screen, never for its hour.
 */
export const getStaffMemberEventsV2 = (events, range, timeZone) => {
  const filtered = [];
  events.forEach(event => {
    const start = asWallClockIn(event.startDate, timeZone);
    const end = asWallClockIn(event.endDate, timeZone);
    if (eventInRangeV2({ ...event, startDate: start }, range)) {
      const appointmentType = event.appointmentType?.parent
        ? event.appointmentType?.parent
        : event.appointmentType;
      filtered.push({
        ...event,
        isProviderEvent: true,
        start,
        end,
        // The instants as stored. Anything reasoning about a real moment rather
        // than a drawn position must use these — a series' start/end dates are
        // converted with the clinic timezone downstream and would shift twice.
        trueStart: new Date(event.startDate),
        trueEnd: new Date(event.endDate),
        title: event.client
          ? parseUsername(
              event.client?.name ||
                formatUsername(event.client?.firstName, event.client?.lastName)
            )
          : '',
        desc: { main: appointmentType?.title },
        color: appointmentType?.headerColor || APPOINTMENT_HEADER_COLORS.ADMIN,
        headerColor:
          appointmentType?.headerColor || APPOINTMENT_HEADER_COLORS.ADMIN,
        bgColor:
          appointmentType?.backgroundColor ||
          toLightTint(
            appointmentType?.headerColor || APPOINTMENT_HEADER_COLORS.ADMIN
          ),
        chipColor: toDarkShade(
          appointmentType?.headerColor || APPOINTMENT_HEADER_COLORS.ADMIN
        ),
        isAdmin: event.appointmentType?.eventType?.name === 'Admin'
      });
    }
  });
  return filtered;
};

export const mapAvailabilityBlocksToEvents = (
  availabilityBlocks,
  locations,
  startOfWeekDate,
  isTemplate = false
) => {
  const events = availabilityBlocks?.map(block => {
    return {
      id: block.id,
      start: generateDateFromBlockTime(
        block.startTime,
        block.dayOfWeek,
        startOfWeekDate
      ),
      end: generateDateFromBlockTime(
        block.endTime,
        block.dayOfWeek,
        startOfWeekDate
      ),
      title: getAvailabilityBlockTitle(block, isTemplate),
      clinic: block.clinics,
      color: getAvailabilityBlockColor(block, locations),
      locationCategory: '',
      type: 'availability',
      availType:
        block.blockId || isBlockType(block.type)
          ? 'block'
          : (block.type || '').toLowerCase()
    };
  });
  return events;
};

const isBlockType = type =>
  ['block availability', 'block'].includes((type || '').toLowerCase());

/**
 * On the DT recurring template a care session is the only block the view tracks
 * as filled or unfilled, so it is the only one that reads as "Open". Every other
 * type exists to hold one-time appointments, which the recurring view does not
 * report on, so those carry their own name alone. Outside that template the
 * labels are unchanged.
 */
const getAvailabilityBlockTitle = (event, isTemplate = false) => {
  const open = name => (isTemplate ? name : `Open ${name}`);
  if (event.blockId || isBlockType(event.type))
    return open('Block Availability');
  const type = (event.type || '').toLowerCase();
  switch (type) {
    case 'working':
    case 'md care sessions':
      return 'Open Care Session';
    case 'dt eval slot':
      return open('DT Eval Slot');
    case 'lunch':
      return 'Lunch';
    case 'notes':
      return 'Notes';
    case 'meeting':
      return 'Meeting';
    default: {
      const label = (event.type || 'Availability').replace(/\b\w/g, c =>
        c.toUpperCase()
      );
      return open(`${label} Slot`);
    }
  }
};

const getAvailabilityBlockColor = (event, locations) => {
  const availabilityColors = availabilityColorsMap();
  return availabilityColors.get(event.type?.toLowerCase());
};

const generateDateFromBlockTime = (timeStr, dayOfWeek, weekStart) => {
  const [hour, mins] = timeStr.split(':');
  const result = moment(weekStart);
  result.hour(hour);
  result.minute(mins);
  result.day(dayOfWeek);

  return result.toDate();
};

export const getStaffMemberNonWorkingHours = (
  workingHours,
  currentRange,
  userId
) => {
  if (!workingHours) return [];
  const momentDayToChar = {
    1: 'mon',
    2: 'tues',
    3: 'wed',
    4: 'thurs',
    5: 'fri',
    6: 'sat',
    0: 'sun'
  };
  let result = [];

  const cur = moment(currentRange.start);
  while (cur.isBefore(currentRange.end)) {
    const day = momentDayToChar[cur.day()];
    if (workingHours[day]) {
      const hours = workingHours[day] || [];
      const workingTimes = [
        [0, 410],
        [1260, 1440]
      ];
      hours.forEach(hourObject => {
        const [startHour, startMin] = hourObject.startTime.split(':');
        const [endHour, endMin] = hourObject.endTime.split(':');
        const start = parseInt(startHour || 0) * 60 + parseInt(startMin || 0);
        const end = parseInt(endHour || 0) * 60 + parseInt(endMin || 0);
        workingTimes.push([start, end]);
      });

      const occupiedSlots = getTimeSlots(
        workingTimes,
        true,
        'quarter',
        false,
        true
      );
      for (let key in occupiedSlots) {
        const [startHour, startMin] = occupiedSlots[key].split(':');
        const start = moment(cur)
          .hour(startHour)
          .minute(startMin)
          .toDate();
        const end = moment(start)
          .add(15, 'minutes')
          .toDate();
        result.push({
          start: start,
          end: end,
          type: 'na',
          userId,
          resourceId: userId
        });
      }
    } else {
      result.push({
        start: cur.hour(MIN_CALENDAR_TIME.getHours()).toDate(),
        end: cur.hour(MAX_CALENDAR_TIME.getHours()).toDate(),
        type: 'na',
        userId,
        resourceId: userId
      });
    }
    cur.add(1, 'day');
  }
  return result;
};

export const mapProviderTableInfo = (
  providerData,
  Select,
  providerAllergies,
  allCertOptions,
  handleStaffUpdate
) => {
  let mappedProviderList = [];
  if (providerData)
    return (mappedProviderList = providerData.map((providerInfo, index) => {
      let {
        clientInfo,
        providerList,
        providerRoasterNotes,
        providerAvailabilities
      } = providerInfo;
      let { providerCapacity } = providerList;
      let { address, speciality, clinic, corticaPrograms } = providerList;
      let futureClientIds = clientInfo.futureClient.map(data => data.clientId);
      let pastClient = clientInfo.pastClient.filter(
        data => !futureClientIds.includes(data.clientId)
      );
      let totalClientCount =
        clientInfo.futureClient?.length + pastClient?.length;
      let providerAddress = formProviderAddress(address, clinic);
      let programType = constructProgramDataForDisplay(corticaPrograms);
      let client_dayAvailabilities = providerDayAvailabilities(
        providerAvailabilities
      );
      let [latestUpdatedAvail] = client_dayAvailabilities;
      let updateAvailabilityDate = latestUpdatedAvail?.updatedAt
        ? moment(latestUpdatedAvail.updatedAt).format('MM/DD/YYYY')
        : 'N/A';
      return {
        providerId: providerList.id,
        name:
          formatProviderName(providerList) +
          ',' +
          ' ' +
          speciality?.abbreviation +
          ' ' +
          `(${providerList?.clinic?.abbreviation || ''})`,
        status: providerList?.status || 'N/A',
        certifications: mapCertificationOptions(
          allCertOptions,
          providerList?.certificationMappings
        ),
        email: providerList?.email,
        phoneNumber: providerList?.mobilePhone || 'N/A',
        zipCode: address?.zipCode || 'N/A',
        gender: providerList.gender || 'N/A',
        programType: programType,
        transportation: providerList?.transportation,
        providerData: providerList,
        petAllergies: providerList.petAllergies,
        languages: providerList.languages,
        actions: (
          <div>
            <img src={ThreeDot} />
          </div>
        ),
        availUpdated: updateAvailabilityDate,
        clients: totalClientCount,
        notes: providerRoasterNotes,
        notesCount: providerRoasterNotes?.length,
        providerCapacity: providerCapacity,
        providerAddress: providerAddress,
        speciality: speciality,
        employment: providerList?.employment,
        isObservable: providerList?.isObservable,
        client_dayAvailabilities: client_dayAvailabilities,
        currentClients: clientInfo.futureClient,
        pastClient: pastClient,
        clinic_timezone: providerList?.clinic?.timezone
      };
    }));
};
const providerDayAvailabilities = ProviderTemplates => {
  let DayAvailabilities = [];
  ProviderTemplates.forEach(IndTemplate => {
    DayAvailabilities.push(...IndTemplate.dayAvailabilities);
  });
  return DayAvailabilities;
};
const formProviderAddress = (address, clinic) => {
  let provider_address = '';
  if (address) {
    provider_address += address?.addressLine1
      ? `${address.addressLine1}, `
      : '';
    provider_address += address?.addressLine2
      ? `${address.addressLine2}, `
      : '';
    provider_address += address?.city ? `${address?.city}, ` : '';
    provider_address += address?.state ? `${address?.state} ` : '';
    provider_address +=
      clinic?.abbreviation && address?.state ? `${clinic?.abbreviation} ` : '';
    provider_address += address?.zipCode || '';
  }

  // Trim any trailing commas and spaces
  provider_address = provider_address.trim().replace(/,\s*$/, '');
  return provider_address;
};
